/**
 * External dependencies
 */
import { cn } from '@Utils/cn';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Internal dependencies
 */

function ReactRouterBreadcrumb(props: React.ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
}

function ReactRouterBreadcrumbList({
  className,
  ...props
}: React.ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        'text-muted-foreground flex flex-wrap items-center gap-1.5 text-sm wrap-break-word sm:gap-2.5',
        className,
      )}
      {...props}
    />
  );
}

function ReactRouterBreadcrumbItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn('inline-flex items-center gap-1.5', className)}
      {...props}
    />
  );
}

type ReactRouterBreadcrumbLinkProps = Omit<
  React.ComponentProps<'a'>,
  'href'
> & {
  to: string;
  asChild?: boolean;
};

function ReactRouterBreadcrumbLink({
  to,
  className,
  ...rest
}: ReactRouterBreadcrumbLinkProps) {
  return (
    <Link
      to={to}
      {...rest}
      data-slot="breadcrumb-link"
      className={cn('hover:text-foreground transition-colors', className)}
    />
  );
}

function ReactRouterBreadcrumbPage({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn('text-foreground font-normal', className)}
      {...props}
    />
  );
}

function ReactRouterBreadcrumbSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn('[&>svg]:size-3.5', className)}
      {...props}
    >
      {children ?? <ChevronRight />}
    </li>
  );
}

function ReactRouterBreadcrumbEllipsis({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn('flex size-9 items-center justify-center', className)}
      {...props}
    >
      <MoreHorizontal className="size-4" />
      <span className="sr-only">More</span>
    </span>
  );
}

export {
  ReactRouterBreadcrumb,
  ReactRouterBreadcrumbEllipsis,
  ReactRouterBreadcrumbItem,
  ReactRouterBreadcrumbLink,
  ReactRouterBreadcrumbList,
  ReactRouterBreadcrumbPage,
  ReactRouterBreadcrumbSeparator,
};
